import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Check, ArrowUpRight, Plus } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { Reveal, MaskHeading } from "@/components/site/motion";
import { QuoteCTA } from "@/components/site/QuoteCTA";
import { useSeo } from "@/components/site/Seo";
import { SERVICES, ALSO_ASSEMBLE } from "@/data/services";

const ServiceRow = ({ s, i }) => {
  const flip = i % 2 === 1;
  return (
    <article id={s.id} className="scroll-mt-28 grid gap-8 lg:gap-16 lg:grid-cols-12 items-center py-16 lg:py-24 border-t border-[var(--line)]" data-testid={`service-${s.id}`}>
      <Reveal className={`lg:col-span-6 ${flip ? "lg:order-2" : ""}`}>
        <div className="group relative clip-cut overflow-hidden aspect-[4/3] bg-[var(--steel)]">
          <img src={s.img} alt={`${s.title} service in Woodstock, Ingersoll and Oxford County`} loading="lazy"
            className="w-full h-full object-cover transition-transform duration-[1400ms] ease-[cubic-bezier(.16,1,.3,1)] group-hover:scale-105" />
          <span className="absolute top-5 left-5 bg-[var(--orange)] text-[var(--ink)] font-mono-x text-xs font-bold px-2.5 py-1">{String(i + 1).padStart(2, "0")} / {String(SERVICES.length).padStart(2, "0")}</span>
        </div>
      </Reveal>
      <Reveal className="lg:col-span-6" delay={0.1}>
        <s.icon className="w-9 h-9 text-[var(--orange)]" strokeWidth={1.75} />
        <h2 className="mt-5 font-display font-black uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.9]">{s.title}</h2>
        <p className="mt-5 text-zinc-300 text-base lg:text-lg leading-relaxed max-w-xl">{s.long}</p>
        <ul className="mt-7 grid sm:grid-cols-2 gap-x-6 gap-y-3">
          {s.items.map((it) => (
            <li key={it} className="flex items-start gap-3 text-zinc-200"><Check className="w-4 h-4 mt-1 text-[var(--orange)] shrink-0" />{it}</li>
          ))}
        </ul>
        <Link to={`/contact?service=${s.id}`} data-testid={`service-quote-${s.id}`} className="btn-orange mt-9 px-6 py-3.5 text-base"><span>Quote my {s.title.split(" ")[0].toLowerCase()} job</span><ArrowUpRight className="w-4 h-4" /></Link>
      </Reveal>
    </article>
  );
};

const OtherJobs = () => (
  <section className="bg-[var(--char)] border-y border-[var(--line)] py-24 lg:py-32" data-testid="other-jobs-section">
    <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12 grid gap-12 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <p className="eyebrow mb-5">Not on the list?</p>
        <MaskHeading lines={[{ text: "Ask us about" }, { text: "any other job.", className: "text-[var(--orange)]" }]}
          className="font-display font-black uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.9]" />
        <Reveal>
          <p className="mt-7 text-zinc-300 text-base lg:text-lg leading-relaxed">
            We help with practically anything you purchase that requires assembly or installation. If it's not listed here, ask — send a short description and a photo and we'll tell you if it's a fit.
          </p>
          <Link to="/contact?service=other" data-testid="ask-other-job-btn" className="btn-orange mt-8 px-7 py-4 text-lg"><span>Ask about my job</span><ArrowUpRight className="w-5 h-5" /></Link>
        </Reveal>
      </div>
      <div className="lg:col-span-7 flex flex-wrap content-start gap-3">
        {ALSO_ASSEMBLE.map((a, i) => (
          <Reveal key={a} delay={i * 0.03} y={16}>
            <span className="inline-flex items-center gap-2 border border-[var(--line)] bg-[var(--ink)] px-5 py-3.5 font-display text-xl lg:text-2xl font-bold uppercase hover:border-[var(--orange)] hover:text-[var(--orange)] transition-colors duration-300">
              <Plus className="w-4 h-4 text-[var(--orange)]" />{a}
            </span>
          </Reveal>
        ))}
        <Reveal delay={0.4} y={16}>
          <span className="inline-flex items-center gap-2 bg-[var(--orange)] text-[var(--ink)] px-5 py-3.5 font-display text-xl lg:text-2xl font-black uppercase">+ Your item here</span>
        </Reveal>
      </div>
    </div>
  </section>
);

export default function Services() {
  const { hash } = useLocation();
  useSeo({
    title: "Assembly & Installation Services | Assemble Pro – Woodstock, Ingersoll, Oxford County",
    description: "Furniture assembly, TV mounting, fitness equipment, BBQ assembly, gazebos, sheds, blinds, shelving and fence gate installation in Woodstock, Ingersoll and Oxford County, Ontario.",
    path: "/services",
  });

  useEffect(() => {
    if (!hash) return undefined;
    const t = setTimeout(() => {
      const el = document.querySelector(hash);
      if (el && window.__lenis) window.__lenis.scrollTo(el, { offset: -90 });
      else if (el) el.scrollIntoView();
    }, 350);
    return () => clearTimeout(t);
  }, [hash]);

  return (
    <div data-testid="services-page">
      <PageHero testId="services-hero" eyebrow="Services · Oxford County, ON"
        lines={[{ text: "Assembly &" }, { text: "Installation", className: "text-[var(--orange)]" }, { text: "Services." }]}
        intro="Nine core categories, one dependable crew. Here's what we assemble and install for homes and businesses across Woodstock, Ingersoll and Oxford County."
        img="/img/craft.jpg" imgAlt="Craftsman working on a wood assembly project" />
      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12 pb-16">
        <nav className="flex gap-2 overflow-x-auto pb-6 -mx-1 px-1" aria-label="Service categories" data-testid="services-jump-nav">
          {SERVICES.map((s) => (
            <a key={s.id} href={`#${s.id}`} onClick={(e) => { e.preventDefault(); const el = document.getElementById(s.id); window.__lenis ? window.__lenis.scrollTo(el, { offset: -90 }) : el.scrollIntoView(); }}
              className="shrink-0 border border-[var(--line)] px-4 py-2 font-mono-x text-xs uppercase tracking-wider text-zinc-300 hover:border-[var(--orange)] hover:text-[var(--orange)] transition-colors">{s.title}</a>
          ))}
        </nav>
        {SERVICES.map((s, i) => <ServiceRow key={s.id} s={s} i={i} />)}
      </div>
      <OtherJobs />
      <QuoteCTA title={["Tell us", "what you bought."]} />
    </div>
  );
}
